import { useTranslation } from "react-i18next";
import {
	ChartCard,
	ColumnChart,
	Donut,
	HBarList,
	type HolatRangi,
	holatVar,
	Legend,
	LineChart,
} from "@/shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	BolimlarRoyxati,
	chiroyliMax,
	HaftaSoatIssiqlik,
	ikkiXona,
	kategoriyaRangi,
	kunlikUstunlar,
	type Qatlam,
	segmentliQatorlar,
	TOP_N,
} from "./shared";

/**
 * Shubhali hodisalar analitikasi — `fake_location` VA `violations`
 * (reja E3). Ikki kind farqi faqat KPI ning to'rtinchi kaliti
 * (`actioned` / `devices`) — `KpiRow` `totals` ni o'zi aylanadi.
 *
 * Tartib: KPI → [kunlar (yuqori — qizil) | turlar donut'i] →
 * [hafta kuni × soat | soatlik chiziq] → [xodimlar | bo'limlar].
 *
 * ⚠️ `flagged` (`severity = 'high'`) — `primary` ning QISMI (`shared.tsx`).
 */

const RANG = {
	oddiy: holatVar("kelgan"),
	yuqori: holatVar("kelmagan"),
	oldingi: holatVar("neytral"),
} as const;

/**
 * `suspicion_kind` → rang. `mock_provider` — qizil (asosiy «soxta
 * joylashuv» belgisi); `jailbreak` bilan bir `fake_location` donut'ida
 * yonma-yon turadi, shuning uchun ular turli rangda. Noma'lum tur —
 * tartib bo'yicha aylanma rang.
 */
const TUR_RANGI: Record<string, HolatRangi> = {
	// biome-ignore lint/style/useNamingConvention: backend suspicion_kind qiymati
	mock_provider: "kelmagan",
	jailbreak: "ertaKetgan",
	speed: "kelgan",
	teleport: "kechikkan",
	// biome-ignore lint/style/useNamingConvention: backend suspicion_kind qiymati
	time_skew: "uzrli",
	// biome-ignore lint/style/useNamingConvention: backend suspicion_kind qiymati
	accuracy_anomaly: "neytral",
};

export function SuspicionAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bosh = t("analytics.generic.empty");
	const oddiyNom = t("analytics.suspicion.normal");
	const yuqoriNom = t("analytics.suspicion.high");
	const qatlam: Qatlam = {
		asosiy: { label: oddiyNom, color: RANG.oddiy },
		belgilangan: { label: yuqoriNom, color: RANG.yuqori },
	};
	const qiymatMatni = (total: string, flagged: string) =>
		t("analytics.suspicion.value", { total, flagged });

	const jami = data.totals.find((x) => x.key === "events")?.value ?? 0;
	const ustunlar = kunlikUstunlar(data.daily, qatlam, (date, base, flagged) =>
		t("analytics.suspicion.dayTooltip", { date, base, flagged })
	);

	const soatMax = chiroyliMax(Math.max(0, ...data.hourly.flatMap((h) => [h.count, h.previous])));
	const soatChoqqi = data.hourly.reduce<(typeof data.hourly)[number] | null>(
		(eng, h) => (h.count > 0 && (eng === null || h.count > eng.count) ? h : eng),
		null
	);
	const joriyNom = t("analytics.events.currentPeriod");
	const oldingiNom = t("analytics.events.previousPeriod");

	return (
		<div data-testid="suspicion-analytics" className="flex flex-col gap-3.5">
			<KpiRow kind={kind} totals={data.totals} spark={data.spark} danger={["high"]} />

			<div className="grid gap-3.5 lg:grid-cols-[1.6fr_1fr]">
				<div data-widget="daily">
					<ChartCard
						title={t("analytics.suspicion.dailyTitle")}
						subtitle={t("analytics.suspicion.dailySubtitle")}
						extra={
							<Legend
								items={[
									{ label: oddiyNom, color: RANG.oddiy },
									{ label: yuqoriNom, color: RANG.yuqori },
								]}
							/>
						}
					>
						<ColumnChart columns={ustunlar} height={200} empty={bosh} />
					</ChartCard>
				</div>

				<div data-widget="breakdown">
					<ChartCard title={t("analytics.suspicion.breakdownTitle")}>
						<Donut
							segments={data.breakdown.map((b, i) => {
								const tur = TUR_RANGI[b.key];
								return {
									key: b.key,
									value: b.count,
									color: tur ? holatVar(tur) : kategoriyaRangi(i),
									label: t(`analytics.breakdown.${b.key}`, { defaultValue: b.key }),
								};
							})}
							centerValue={sonMatni(jami)}
							centerLabel={t("analytics.suspicion.breakdownCenter")}
							formatValue={sonMatni}
							empty={bosh}
						/>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-[1.4fr_1fr]">
				<div data-widget="heat">
					<ChartCard
						title={t("analytics.events.heatTitle")}
						subtitle={t("analytics.suspicion.heatSubtitle")}
					>
						<HaftaSoatIssiqlik heat={data.heat} />
					</ChartCard>
				</div>

				<div data-widget="hourly">
					<ChartCard
						title={t("analytics.events.hourlyTitle")}
						subtitle={t("analytics.events.hourlySubtitle")}
					>
						<LineChart
							xLabels={data.hourly.map((h) => ikkiXona(h.hour))}
							series={[
								{
									key: "current",
									label: joriyNom,
									color: RANG.oddiy,
									values: data.hourly.map((h) => h.count),
									area: true,
								},
								{
									key: "previous",
									label: oldingiNom,
									color: RANG.oldingi,
									values: data.hourly.map((h) => h.previous),
									dashed: true,
								},
							]}
							yMin={0}
							yMax={soatMax}
							yTicks={[0, soatMax / 2, soatMax]}
							formatY={sonMatni}
							height={160}
							empty={bosh}
						/>
						{/* ⚠️ Seriyalar legendasini `LineChart` o'zi chizadi — bu yerda faqat cho'qqi. */}
						<div className="mt-1">
							{soatChoqqi ? (
								<span className="text-xs" style={{ color: "var(--chart-axis)" }}>
									{t("analytics.events.hourlyPeak", {
										hour: ikkiXona(soatChoqqi.hour),
										count: sonMatni(soatChoqqi.count),
									})}
								</span>
							) : null}
						</div>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-2">
				<div data-widget="top-employees">
					<ChartCard
						title={t("analytics.suspicion.topTitle")}
						subtitle={t("analytics.suspicion.topSubtitle")}
						extra={
							<Legend
								items={[
									{ label: oddiyNom, color: RANG.oddiy },
									{ label: yuqoriNom, color: RANG.yuqori },
								]}
							/>
						}
					>
						<HBarList
							empty={bosh}
							rows={segmentliQatorlar(
								data.topEmployees.slice(0, TOP_N),
								qatlam,
								(x) => x.employeeId,
								(x) => x.employeeName,
								qiymatMatni
							)}
						/>
					</ChartCard>
				</div>

				<div data-widget="departments">
					<ChartCard title={t("analytics.events.departmentsTitle")}>
						<BolimlarRoyxati rows={data.byDepartment} qatlam={qatlam} qiymatMatni={qiymatMatni} />
					</ChartCard>
				</div>
			</div>
		</div>
	);
}
